/**
 * Workflow action creators for the claims dashboard.
 *
 * Split from claimsDashboardActionCreator.js for maintainability.
 */
import { toastr } from 'react-redux-toastr';
import { makeApiCall } from '../../../../../services/apiService/apiService';

export const createClaimRequest = (
  payload,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const formData = new FormData();
  formData.append('cnote', payload.cnote || '');
  formData.append('reasonId', payload.reasonId || '');
  formData.append('requestedAmount', payload.requestedAmount || '');
  if (payload.invoiceNumbers && Array.isArray(payload.invoiceNumbers)) {
    payload.invoiceNumbers.forEach((inv, index) => {
      formData.append(`invoiceNumbers[${index}]`, inv);
    });
  }
  if (
    payload.affectedBoxCount !== undefined &&
    payload.affectedBoxCount !== null
  ) {
    formData.append('affectedBoxCount', payload.affectedBoxCount);
  }
  if (
    payload.affectedPieceCount !== undefined &&
    payload.affectedPieceCount !== null
  ) {
    formData.append('affectedPieceCount', payload.affectedPieceCount);
  }
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.remarks) {
    formData.append('remarks', payload.remarks);
  }
  if (payload.attachments && Array.isArray(payload.attachments)) {
    payload.attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'createClaimRequest',
    method: 'POST',
    postFormData: true,
    data: formData,
    successCb: res => {
      if (
        res.status === 'SUCCESS' ||
        res.statusCode === 200 ||
        res.statusCode === 0
      ) {
        toastr.success('Success', 'Claim request created successfully');
        successCallback(res.payload || {});
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not create claim request'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not create claim request');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};

export const updateClaimRequest = (
  claimId,
  payload,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const formData = new FormData();
  formData.append('cnote', payload.cnote || '');
  formData.append('reasonId', payload.reasonId || '');
  formData.append('requestedAmount', payload.requestedAmount || '');

  if (payload.invoiceNumbers && Array.isArray(payload.invoiceNumbers)) {
    payload.invoiceNumbers.forEach((inv, index) => {
      formData.append(`invoiceNumbers[${index}]`, inv);
    });
  }
  if (
    payload.affectedBoxCount !== undefined &&
    payload.affectedBoxCount !== null
  ) {
    formData.append('affectedBoxCount', payload.affectedBoxCount);
  }
  if (
    payload.affectedPieceCount !== undefined &&
    payload.affectedPieceCount !== null
  ) {
    formData.append('affectedPieceCount', payload.affectedPieceCount);
  }
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.remarks) {
    formData.append('remarks', payload.remarks);
  }
  if (payload.attachments && Array.isArray(payload.attachments)) {
    payload.attachments.forEach((file, index) => {
      formData.append(`attachments[${index}]`, file);
    });
  }
  if (payload.s3Urls && Array.isArray(payload.s3Urls)) {
    payload.s3Urls.forEach((url, index) => {
      formData.append(`s3Urls[${index}]`, url);
    });
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'updateClaimRequest',
    method: 'POST',
    postFormData: true,
    data: formData,
    pathVariables: [{ key: '{id}', val: claimId }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'Claim request updated successfully');
        successCallback(res.payload || {});
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not update claim request'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not update claim request');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};

export const clientApproveClaimAction = (
  claimId,
  payload,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const formData = new FormData();
  if (payload.requestedAmount != null) {
    formData.append('requestedAmount', payload.requestedAmount);
  }
  if (payload.description) {
    formData.append('description', payload.description);
  }
  if (payload.remarks) {
    formData.append('remarks', payload.remarks);
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'clientApproveClaim',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    postFormData: true,
    data: formData,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'Claim approved successfully');
        successCallback(res.payload || {});
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not approve claim'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not approve claim');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};

export const performWorkflowAction = (
  claimId,
  payload,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  // Check if the payload contains File objects (e.g. rcaAttachments)
  let hasFiles = false;
  const checkFiles = obj => {
    if (!obj || typeof obj !== 'object') return;
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i += 1) {
      const key = keys[i];
      const val = obj[key];
      if (val instanceof File) {
        hasFiles = true;
        return;
      }
      if (Array.isArray(val)) {
        if (val.length > 0 && val[0] instanceof File) {
          hasFiles = true;
          return;
        }
        for (let idx = 0; idx < val.length; idx += 1) {
          const item = val[idx];
          if (item instanceof File) {
            hasFiles = true;
          } else if (typeof item === 'object') {
            checkFiles(item);
          }
        }
      } else if (typeof val === 'object') {
        checkFiles(val);
      }
    }
  };
  checkFiles(payload);

  let requestBody;
  let contentType;

  if (hasFiles) {
    // Use FormData for multipart/form-data (supports file uploads)
    const formData = new FormData();
    const appendFormData = (form, key, val) => {
      if (val === null || val === undefined) return;
      if (val instanceof File) {
        form.append(key, val);
      } else if (Array.isArray(val)) {
        val.forEach((item, i) => {
          if (item instanceof File) {
            form.append(key, item);
          } else if (typeof item === 'object' && item !== null) {
            appendFormData(form, `${key}[${i}]`, item);
          } else {
            form.append(`${key}[${i}]`, item);
          }
        });
      } else if (typeof val === 'object') {
        const subKeys = Object.keys(val);
        for (let j = 0; j < subKeys.length; j += 1) {
          const subKey = subKeys[j];
          const subVal = val[subKey];
          appendFormData(form, key ? `${key}.${subKey}` : subKey, subVal);
        }
      } else {
        form.append(key, val);
      }
    };

    const payloadKeys = Object.keys(payload);
    for (let k = 0; k < payloadKeys.length; k += 1) {
      const key = payloadKeys[k];
      appendFormData(formData, key, payload[key]);
    }
    requestBody = formData;
    contentType = undefined; // let browser set multipart boundary
  } else {
    // Use URL-encoded form params (original behavior)
    const flattenToFormParams = (obj, prefix, params) => {
      if (obj === null || obj === undefined) return;
      for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const val = obj[key];
        const paramKey = prefix ? `${prefix}.${key}` : key;

        if (val === null || val === undefined) {
        } else if (Array.isArray(val)) {
          val.forEach((item, i) => {
            if (typeof item === 'object' && item !== null) {
              flattenToFormParams(item, `${paramKey}[${i}]`, params);
            } else {
              params.push(
                `${encodeURIComponent(
                  `${paramKey}[${i}]`
                )}=${encodeURIComponent(item)}`
              );
            }
          });
        } else if (typeof val === 'object') {
          flattenToFormParams(val, paramKey, params);
        } else {
          params.push(
            `${encodeURIComponent(paramKey)}=${encodeURIComponent(val)}`
          );
        }
      }
    };

    const params = [];
    flattenToFormParams(payload, '', params);
    requestBody = params.join('&');
    contentType = 'application/x-www-form-urlencoded';
  }

  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'performWorkflowAction',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    postFormData: true,
    data: requestBody,
    successCb: res => {
      if (res && res.status === 'SUCCESS') {
        toastr.success('Success', 'Action completed successfully');
        successCallback(res.payload || {});
      } else if (res && res.status !== 'FAILURE') {
        toastr.error(
          'Action Failed',
          res.errorMessage ||
            res.message ||
            'The server could not process this request.'
        );
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not perform action'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not perform action');
      settledCallback();
    }
  };

  if (contentType) {
    requestObj.contentType = contentType;
  }

  return makeApiCall(requestObj);
};

export const reassignUser = (
  claimId,
  newUserId,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'reassignUser',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    replaceParamsArr: [{ key: 'newUserId', val: newUserId }],
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'User reassigned successfully');
        successCallback(res.payload || {});
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not reassign user'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not reassign user');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};

export const submitRca = (
  claimId,
  payload,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'submitRca',
    method: 'POST',
    pathVariables: [{ key: '{id}', val: claimId }],
    data: payload,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        toastr.success('Success', 'RCA submitted successfully');
        successCallback(res.payload || {});
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && res.errorMessage) || 'Could not submit RCA'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not submit RCA');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};

export const bulkApprove = (
  claimIds,
  successCallback = () => {},
  settledCallback = () => {}
) => () => {
  const requestObj = {
    baseUrl: 'zoomApiUrl',
    apiEndPoint: 'bulkApprove',
    method: 'POST',
    data: claimIds,
    successCb: res => {
      if (res.status === 'SUCCESS') {
        const payload = res.payload || {};
        const { totalRequested, totalApproved } = payload;
        let message =
          'The COF(s) will be generated. Please check after sometime.';

        if (totalRequested !== undefined && totalApproved !== undefined) {
          message = `Successfully generated COF for ${totalApproved} out of ${totalRequested} requests.`;
        }

        toastr.success('Success', message);
        successCallback(payload);
      }
      settledCallback();
    },
    failureCb: res => {
      toastr.error(
        'Something went wrong!',
        (res && (res.errorMessage || res.message || res.error)) ||
          'Could not perform bulk approval'
      );
      settledCallback();
    },
    catchCb: () => {
      toastr.error('Something went wrong!', 'Could not perform bulk approval');
      settledCallback();
    }
  };

  return makeApiCall(requestObj);
};
